import { useEffect, useMemo } from 'react';
import { useFetch } from '../common/hooks/useFetch';
import { usePolling } from '../common/hooks/usePolling';
import { buildPublicStandingsUrl } from '../common/utils/api.utils';
import { POLLING_INTERVALS } from '../constants/timing.constants';
import { isValidParam } from '../common/utils/validation';
import type {
  PublicStandingsAPIResponse,
  CompetitionSeason,
} from '../types/api/publicStandings.types';
import type { FetchState } from '../types/common/fetchState.types';
import { isFetchSuccess } from '../types/common/fetchState.types';

export interface UsePublicStandingsOptions {
  /**
   * Season key slug (e.g., 'academy-division-26')
   */
  key: string;
}

export interface UsePublicStandingsResult {
  /**
   * Current fetch state (discriminated union)
   * Check status field to access data, error, or loading state
   */
  state: FetchState<PublicStandingsAPIResponse>;

  /**
   * Manual refetch function
   * Useful for user-triggered refresh or error recovery
   */
  refetch: () => Promise<void>;

  /**
   * Convenience boolean flags
   */
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;

  /**
   * Convenience data accessor
   * Returns null when data is not available
   */
  competitionSeason: CompetitionSeason | null;
}

/**
 * Hook for fetching public standings data with auto-polling
 *
 * Composes useFetch and usePolling:
 * - Fetches standings data from S3/CloudFront for a single season key
 * - Skips fetching (and polling) entirely while the key is invalid
 * - Auto-refreshes every 2 minutes
 * - Refetches when the key changes (e.g. League/Flex switch)
 */
export const usePublicStandings = ({
  key,
}: UsePublicStandingsOptions): UsePublicStandingsResult => {
  // Empty URL while the key is invalid — the effect below skips empty URLs
  const url = useMemo(
    () => (isValidParam(key) ? buildPublicStandingsUrl(key) : ''),
    [key]
  );

  // Fetch hook (does not auto-fetch, we control it with useEffect)
  const { state, fetchData, isLoading, isError, isSuccess } =
    useFetch<PublicStandingsAPIResponse>({
      url,
    });

  // Initial fetch on mount and whenever the URL (key) changes
  useEffect(() => {
    if (url) {
      fetchData();
    }
  }, [url, fetchData]);

  // Polling hook for automatic updates
  usePolling({
    callback: fetchData,
    interval: POLLING_INTERVALS.PUBLIC_STANDINGS,
    enabled: Boolean(url),
    runOnMount: false, // Initial fetch handled by useEffect above
  });

  return {
    state,
    refetch: fetchData,
    isLoading,
    isError,
    isSuccess,
    // Available on success and during background refreshes with cached data
    competitionSeason: isFetchSuccess(state)
      ? state.data.competition_season
      : (state.data?.competition_season ?? null),
  };
};
